import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import useEmailStore from "@store/useEmailStore";
import emailService from "@services/email.service";
import usePlatformStore from "@store/usePlatformStore";
import useSystemStore from "@store/useSystemStore";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { ActiveScreen } from "@enums/screens.enum";
import {
  EMAIL_QUERY_KEY,
  REMOVE_THREAD_MUTATION_KEY,
  THREAD_AI_CONTEXT_QUERY_KEY,
  THREAD_ATTACHMENTS_QUERY_KEY,
  THREAD_QUERY_KEY,
} from "@config/query";
import { IEmailResponse } from "@interfaces/responses.interface";

const THREAD_SCOPED_QUERY_KEYS = [
  THREAD_QUERY_KEY,
  THREAD_AI_CONTEXT_QUERY_KEY,
  THREAD_ATTACHMENTS_QUERY_KEY,
];

export function useRemoveThread() {
  const { itemId } = usePlatformStore();
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { changeActiveScreen } = useSystemStore();
  const { threadId, setEmailId, setThreadId } = useEmailStore();

  return useMutation({
    mutationKey: [REMOVE_THREAD_MUTATION_KEY],
    mutationFn: async () => {
      return await emailService.removeThread(threadId);
    },
    onSuccess: (response) => {
      setEmailId(null);
      setThreadId(null);
      changeActiveScreen(ActiveScreen.SYNC_EMAIL);

      // useGetEmail's effect restores the deleted threadId from this cache entry otherwise.
      queryClient.setQueryData([EMAIL_QUERY_KEY, itemId], (old: IEmailResponse) =>
        old ? { ...old, id: null, thread: null } : old
      );

      THREAD_SCOPED_QUERY_KEYS.forEach((key) => {
        void queryClient.cancelQueries({ queryKey: [key, threadId] });
        queryClient.removeQueries({ queryKey: [key, threadId] });
      });

      ToastService.showSuccess("Thread Successfully Removed");
      ToastService.dismiss("sync-when-sent");
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.REMOVE_THREAD);
      ToastService.showError(error as ApiError, ERROR_MESSAGES.REMOVE_THREAD);
    },
  });
}
